import { Crown, Sparkles, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { cn } from "@/lib/utils";

interface SubscriptionStatusBadgeProps {
  compact?: boolean;
  className?: string;
}

export function SubscriptionStatusBadge({ compact = false, className }: SubscriptionStatusBadgeProps) {
  const { plan, hasActiveSubscription, daysRemaining, isLoading, isTrialExpired } = useTrialStatus();

  if (isLoading) {
    return (
      <div className={cn("animate-pulse h-6 w-20 bg-muted rounded", className)} />
    );
  }

  const getPlanDisplay = () => {
    if (!plan) return "Starter";
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const getStatusInfo = () => {
    if (hasActiveSubscription) {
      return { 
        label: "Active", 
        variant: "success" as const,
        icon: Crown
      };
    }
    if (daysRemaining !== null && daysRemaining > 0) {
      return { 
        label: compact ? `${daysRemaining}d` : `Trial (${daysRemaining}d)`, 
        variant: "warning" as const,
        icon: Sparkles
      };
    }
    return { 
      label: "Expired", 
      variant: "destructive" as const,
      icon: AlertTriangle
    };
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  const variantStyles = {
    success: "bg-success/10 text-success border-success/50/20",
    warning: "bg-warning/10 text-warning border-warning/50/20",
    destructive: "bg-destructive/10 text-destructive border-destructive/20"
  };

  if (compact) {
    return (
      <Link 
        to="/dashboard/settings?tab=billing"
        className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded border transition-colors hover:opacity-80",
          variantStyles[statusInfo.variant],
          className
        )}
      >
        <Icon className="h-2.5 w-2.5" />
        <span>{getPlanDisplay()}</span>
      </Link>
    );
  }

  return (
    <Link 
      to="/dashboard/settings?tab=billing"
      className={cn(
        "inline-flex items-center gap-2 px-3.5 py-1 text-xs font-medium rounded-xl border transition-colors hover:opacity-80 whitespace-nowrap",
        variantStyles[statusInfo.variant],
        className
      )}
    >
      <Icon className="h-3 w-3" />
      <span>{getPlanDisplay()}</span>
      <span className="opacity-60">•</span>
      <span>{statusInfo.label}</span>
    </Link>
  );
}
